import React, { useState, useMemo } from "react";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Button,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  Alert,
  CircularProgress,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import FilterListIcon from "@mui/icons-material/FilterList";

import { RiskBreakdownChart } from "../../../../../shared/components/charts";
import {
  CohortGuard,
  DashboardCard,
  DashboardCardSkeleton,
  RiskScoringMethodDetails,
} from "../../../../../shared/components";
import {
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  DateRange,
} from "../../../../../shared/components";
import {
  useJeopardyInsightsStats,
  useAtRiskUsers,
  useCardiovascularJeopardy,
} from "../hooks/useJeopardyInsights";
import { buildFilterParams, roundToDecimal } from "../../shared/utils";
import { useClientScope } from "../../../../../shared/contexts";
import {
  usePersistedDateRange,
  usePersistedFilters,
} from "../../../../../shared/hooks";
import { ROUTES } from "../../../../../shared/utils";
import { Link as RouterLink } from "react-router-dom";
import type { AtRiskUser as ApiAtRiskUser, CardiovascularUser } from "../api/types";

const RISK_LOGIC_TOOLTIP =
  "Risk score is calculated by averaging four banded risk components (Recovery, Sleep, Strain, HRV), then mapped to Low/Medium/High.";

export const JeopardyInsights: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId, isCentralAdmin, isAnonymised } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters, setFilters } = usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filters);
  const { dateRange, setDateRange } = usePersistedDateRange();
  const [riskFilter, setRiskFilter] = useState<string>("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [cvStatusFilter, setCvStatusFilter] = useState<string>("all");
  const [cvPage, setCvPage] = useState(0);
  const [cvPageSize, setCvPageSize] = useState(25);

  const filterParams = useMemo(
    () => buildFilterParams(filters, dateRange, effectiveClientId),
    [filters, dateRange, effectiveClientId]
  );

  const {
    data: statsData,
    isLoading: statsLoading,
    error: statsError,
  } = useJeopardyInsightsStats(filterParams);

  const {
    data: atRiskUsersData,
    isLoading: atRiskUsersLoading,
    error: atRiskUsersError,
  } = useAtRiskUsers({
    risk:
      riskFilter === "all"
        ? undefined
        : (riskFilter as "high" | "medium" | "low"),
    start_date: filterParams.start_date,
    end_date: filterParams.end_date,
    page: page + 1,
    page_size: pageSize,
    department: filterParams.department,
    client_id: filterParams.client_id ?? undefined,
    age_range: filterParams.age_range,
    sex: filterParams.sex,
    shift_pattern: filterParams.shift_pattern,
    user_ids: filterParams.user_ids,
  });

  const {
    data: cvData,
    isLoading: cvLoading,
    error: cvError,
  } = useCardiovascularJeopardy({
    status:
      cvStatusFilter === "all"
        ? undefined
        : (cvStatusFilter as "red" | "amber"),
    start_date: filterParams.start_date,
    end_date: filterParams.end_date,
    page: cvPage + 1,
    page_size: cvPageSize,
    department: filterParams.department,
    client_id: filterParams.client_id ?? undefined,
    age_range: filterParams.age_range,
    sex: filterParams.sex,
    shift_pattern: filterParams.shift_pattern,
    user_ids: filterParams.user_ids,
  });

  const handleApplyFilters = (newFilters: FilterState) => {
    setFilters(newFilters);
    setPage(0);
    setCvPage(0);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    setPage(0);
    setCvPage(0);
  };

  const riskData = useMemo(() => {
    const totalActive = statsData?.data?.total_active_employees?.value || 0;
    const highRiskPercent = roundToDecimal(
      statsData?.data?.high_risk_employees?.value || 0
    );
    const mediumRiskPercent = roundToDecimal(
      statsData?.data?.medium_risk_employees?.value || 0
    );
    const lowRiskPercent = roundToDecimal(
      statsData?.data?.low_risk_employees?.value || 0
    );

    const highRiskCount = Math.round((totalActive * highRiskPercent) / 100);
    const mediumRiskCount = Math.round((totalActive * mediumRiskPercent) / 100);
    const lowRiskCount = Math.round((totalActive * lowRiskPercent) / 100);

    return {
      high: highRiskCount,
      medium: mediumRiskCount,
      low: lowRiskCount,
    };
  }, [statsData]);

  const totalUsers = statsData?.data?.total_active_employees?.value || 0;
  const activeUserCount = statsData?.data?.total_active_employees?.value;
  const participationRate = roundToDecimal(
    statsData?.data?.wellness_participation_rate?.value ||
      statsData?.data?.participation_rate?.value ||
      0
  );

  const atRiskUsers = useMemo(() => {
    if (!atRiskUsersData?.data?.users) return [];
    return atRiskUsersData.data.users.map((user: ApiAtRiskUser) => ({
      id: user.department_id ?? user.user_id,
      userId: user.user_id,
      name: user.name,
      activityStatus: user.activity_status ?? "Active",
      riskLevel: (user.risk_level.charAt(0).toUpperCase() +
        user.risk_level.slice(1)) as "High" | "Medium" | "Low",
      riskScore: user.risk_score,
      recovery: user.recovery,
      sleep: user.sleep,
      strain: user.strain,
      hrv: user.hrv,
      daysInRed: user.days_in_red,
      memberCount: user.member_count ?? null,
    }));
  }, [atRiskUsersData]);

  const atRiskMode = atRiskUsersData?.data?.mode ?? "user";

  // Central admins always see individual names even if the client has anonymisation enabled.
  const anonymised =
    atRiskUsersData?.data?.anonymised === true && !isCentralAdmin;
  const cvAnonymised =
    cvData?.data?.anonymised === true && !isCentralAdmin;

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangePageSize = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRiskFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: string | null
  ) => {
    if (newValue !== null) {
      setRiskFilter(newValue);
      setPage(0);
    }
  };

  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case "high":
        return theme.palette.error.main;
      case "medium":
        return theme.palette.warning.main;
      case "low":
        return theme.palette.success.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined) {
      return "0";
    }
    const rounded = Math.round(value * 100) / 100;
    return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(2);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <DurationSelector
              value={dateRange}
              onChange={handleDateRangeChange}
              buttonVariant="outlined"
            />
            <Button
              variant="text"
              size="medium"
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? "primary" : "inherit"}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : "FILTER"}
            </Button>
          </Box>
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          initialFilters={filters}
        />

        {statsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load jeopardy insights statistics. Please try again.
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            {statsLoading ? (
              <DashboardCardSkeleton title subtitle height={200} />
            ) : (
              <DashboardCard
                title="Employees at risk based on Data"
                subtitle="Risk breakdown based on wellness metrics"
                tooltip={RISK_LOGIC_TOOLTIP}
                detailsContent={<RiskScoringMethodDetails />}
              >
                <CohortGuard
                  userCount={activeUserCount}
                  bypass={isCentralAdmin || !isAnonymised}
                  loading={statsLoading}
                >
                  <RiskBreakdownChart
                    data={riskData}
                    total={totalUsers}
                    participationRate={participationRate}
                    height={200}
                    hideTotal={true}
                    fullWidthLegend={true}
                  />
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <CohortGuard
              userCount={activeUserCount}
              bypass={isCentralAdmin || !isAnonymised}
              loading={statsLoading}
            >
            <DashboardCard
              title="At-Risk Users Detailed View"
              subtitle="Following is the jeopardy view for at risk users"
              tooltip={RISK_LOGIC_TOOLTIP}
              detailsContent={<RiskScoringMethodDetails />}
              titleComponent={
                <ToggleButtonGroup
                  value={riskFilter}
                  exclusive
                  onChange={handleRiskFilterChange}
                  size="small"
                >
                  <ToggleButton value="all">All</ToggleButton>
                  <ToggleButton value="high">High Risk</ToggleButton>
                  <ToggleButton value="medium">Medium Risk</ToggleButton>
                  <ToggleButton value="low">Low Risk</ToggleButton>
                </ToggleButtonGroup>
              }
            >
              <Alert severity="info" sx={{ mb: 2 }}>
                Scores are calculated across the selected date range, not on a
                fixed weekly or monthly batch. Active status shows whether data
                exists in that window.
              </Alert>
              {anonymised && (
                <Alert severity="warning" icon={<LockIcon />} sx={{ mb: 2 }}>
                  Individual user details are not available due to User
                  Anonymisation being enabled for this organisation. Speak to
                  PEAK4 to get this enabled.
                </Alert>
              )}
              {atRiskUsersLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 200,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : atRiskUsersError ? (
                <Alert severity="error">
                  Failed to load at-risk users. Please try again.
                </Alert>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Risk Level</TableCell>
                          <TableCell>Risk Score</TableCell>
                          <TableCell>Recovery</TableCell>
                          <TableCell>Sleep</TableCell>
                          <TableCell>Strain</TableCell>
                          <TableCell>HRV</TableCell>
                          <TableCell>Days in Red</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {atRiskUsers.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={9} align="center">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No at-risk users found for the selected filters.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          atRiskUsers.map((user) => (
                            <TableRow key={user.id} hover>
                              <TableCell>
                                {anonymised ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <LockIcon
                                      sx={{
                                        fontSize: "1rem",
                                        color: "text.disabled",
                                        flexShrink: 0,
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      color="text.disabled"
                                      sx={{ fontStyle: "italic" }}
                                    >
                                      Not available due to User Anonymisation
                                      enabled — Speak to PEAK4 to get this
                                      enabled
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Box
                                    {...(atRiskMode === "user" && user.userId
                                      ? {
                                          component: RouterLink,
                                          to: `${ROUTES.USER_PROGRESS}?userId=${user.userId}`,
                                        }
                                      : {})}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1.5,
                                      textDecoration: "none",
                                      color: "inherit",
                                      ...(atRiskMode === "user" && user.userId
                                        ? {
                                            cursor: "pointer",
                                            "&:hover": { textDecoration: "underline" },
                                          }
                                        : {}),
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: theme.palette.action.active,
                                        fontSize: "0.75rem",
                                      }}
                                    >
                                      {getInitials(user.name)}
                                    </Avatar>
                                    <Typography variant="body2">
                                      {user.name}
                                    </Typography>
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={user.activityStatus}
                                  size="small"
                                  color={
                                    user.activityStatus.toLowerCase() === "active"
                                      ? "success"
                                      : "default"
                                  }
                                  variant={
                                    user.activityStatus.toLowerCase() === "active"
                                      ? "filled"
                                      : "outlined"
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={user.riskLevel}
                                  size="small"
                                  sx={{
                                    bgcolor: getRiskLevelColor(user.riskLevel),
                                    color: "white",
                                    fontWeight: 600,
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {formatNumber(user.riskScore)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {formatNumber(user.recovery)}%
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {formatNumber(user.sleep)}%
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {formatNumber(user.strain)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {formatNumber(user.hrv)}ms
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={formatNumber(user.daysInRed)}
                                  size="small"
                                  color="error"
                                />
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {atRiskUsersData?.data && (
                    <TablePagination
                      component="div"
                      count={atRiskUsersData.data.total}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={pageSize}
                      onRowsPerPageChange={handleChangePageSize}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                  )}
                </>
              )}
            </DashboardCard>
            </CohortGuard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <CohortGuard
              userCount={activeUserCount}
              bypass={isCentralAdmin || !isAnonymised}
              loading={statsLoading}
            >
            <DashboardCard
              title="Cardiovascular Jeopardy"
              subtitle={`Users with avg HRV < 20ms or avg Resting HR > 80bpm over the selected period. Red = both conditions; Amber = either condition.`}
              titleComponent={
                <ToggleButtonGroup
                  value={cvStatusFilter}
                  exclusive
                  onChange={(_, v) => {
                    if (v !== null) {
                      setCvStatusFilter(v);
                      setCvPage(0);
                    }
                  }}
                  size="small"
                >
                  <ToggleButton value="all">All</ToggleButton>
                  <ToggleButton value="red">Red</ToggleButton>
                  <ToggleButton value="amber">Amber</ToggleButton>
                </ToggleButtonGroup>
              }
            >
              {cvAnonymised && (
                <Alert severity="warning" icon={<LockIcon />} sx={{ mb: 2 }}>
                  Individual user details are not available due to User
                  Anonymisation being enabled for this organisation. Showing
                  department-level data.
                </Alert>
              )}
              {cvLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 200,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : cvError ? (
                <Alert severity="error">
                  Failed to load cardiovascular jeopardy data. Please try again.
                </Alert>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            {cvData?.data?.mode === "department" ? "Department" : "User"}
                          </TableCell>
                          {cvData?.data?.mode === "department" && (
                            <TableCell>Members</TableCell>
                          )}
                          <TableCell>Avg HRV (ms)</TableCell>
                          <TableCell>Avg Resting HR (bpm)</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {!cvData?.data?.users?.length ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <Typography variant="body2" color="text.secondary">
                                No cardiovascular jeopardy entries found for the selected filters.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          (cvData.data.users as CardiovascularUser[]).map((user, idx) => (
                            <TableRow
                              key={user.user_id ?? user.department_id ?? idx}
                              hover
                            >
                              <TableCell>
                                {cvAnonymised ? (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <LockIcon
                                      sx={{ fontSize: "1rem", color: "text.disabled", flexShrink: 0 }}
                                    />
                                    <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
                                      Anonymised
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Box
                                    {...(cvData.data.mode !== "department" && user.user_id
                                      ? {
                                          component: RouterLink,
                                          to: `${ROUTES.USER_PROGRESS}?userId=${user.user_id}`,
                                        }
                                      : {})}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1.5,
                                      textDecoration: "none",
                                      color: "inherit",
                                      ...(cvData.data.mode !== "department" && user.user_id
                                        ? {
                                            cursor: "pointer",
                                            "&:hover": { textDecoration: "underline" },
                                          }
                                        : {}),
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: theme.palette.action.active,
                                        fontSize: "0.75rem",
                                      }}
                                    >
                                      {getInitials(user.name)}
                                    </Avatar>
                                    <Typography variant="body2">{user.name}</Typography>
                                  </Box>
                                )}
                              </TableCell>
                              {cvData.data.mode === "department" && (
                                <TableCell>
                                  <Typography variant="body2">
                                    {user.member_count ?? "—"}
                                  </Typography>
                                </TableCell>
                              )}
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color:
                                      user.avg_hrv < 20
                                        ? theme.palette.error.main
                                        : "inherit",
                                    fontWeight: user.avg_hrv < 20 ? 600 : 400,
                                  }}
                                >
                                  {formatNumber(user.avg_hrv)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color:
                                      user.avg_rhr > 80
                                        ? theme.palette.error.main
                                        : "inherit",
                                    fontWeight: user.avg_rhr > 80 ? 600 : 400,
                                  }}
                                >
                                  {formatNumber(user.avg_rhr)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={user.cv_status === "red" ? "Red" : "Amber"}
                                  size="small"
                                  sx={{
                                    bgcolor:
                                      user.cv_status === "red"
                                        ? theme.palette.error.main
                                        : theme.palette.warning.main,
                                    color: "white",
                                    fontWeight: 600,
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {cvData?.data && (
                    <TablePagination
                      component="div"
                      count={cvData.data.total}
                      page={cvPage}
                      onPageChange={(_e, p) => setCvPage(p)}
                      rowsPerPage={cvPageSize}
                      onRowsPerPageChange={(e) => {
                        setCvPageSize(parseInt(e.target.value, 10));
                        setCvPage(0);
                      }}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                  )}
                </>
              )}
            </DashboardCard>
            </CohortGuard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
